'use client';

/**
 * ItemCollectionPicker
 *
 * Displays the lists containing this item as clickable tag chips.
 * A "+" chip opens an inline autocomplete input to add the item to another list.
 * If the typed name doesn't match any existing collection, a "Create new list"
 * option appears — creating it with private visibility and no description.
 *
 * Data:
 *  - All owned collections: GET /api/collections
 *  - Collections with item: GET /api/collections?itemId=<id>
 */

import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { Plus, X, Trash2 } from 'lucide-react';
import {
  fetchCollections,
  fetchCollectionsContainingItem,
  addCollectionItem,
  createCollection,
  removeCollectionItem,
} from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface ItemCollectionPickerProps {
  itemId: string;
}

export function ItemCollectionPicker({ itemId }: ItemCollectionPickerProps) {
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // All owned collections — only fetched once the picker is opened
  const { data: allData, isLoading: loadingAll } = useQuery({
    queryKey: ['collections', 'mine'],
    queryFn: () => fetchCollections({ mine: true }),
    enabled: pickerOpen,
  });

  // Subset already containing this item — always needed to show the chip row
  const { data: containsData, isLoading: loadingContains } = useQuery({
    queryKey: ['collectionsContaining', itemId],
    queryFn: () => fetchCollectionsContainingItem(itemId),
  });

  const { mutate: addToCollection, isPending: addPending } = useMutation({
    mutationFn: (collectionId: string) => addCollectionItem(collectionId, { itemId }),
    onSuccess: (_data, collectionId) => {
      queryClient.invalidateQueries({ queryKey: ['collectionsContaining', itemId] });
      const name = allData?.collections.find((c) => c._id === collectionId)?.title ?? 'list';
      toast.success(`Added to "${name}"`);
      closePicker();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to add item'),
  });

  const { mutate: removeFromCollection, isPending: removePending } = useMutation({
    mutationFn: ({ collectionId, collectionItemId }: { collectionId: string; collectionItemId: string }) =>
      removeCollectionItem(collectionId, collectionItemId),
    onSuccess: (_data, { collectionId }) => {
      queryClient.invalidateQueries({ queryKey: ['collectionsContaining', itemId] });
      const name = containsData?.collections.find((c) => c._id === collectionId)?.title ?? 'list';
      toast.success(`Removed from "${name}"`);
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to remove item'),
  });

  const { mutate: createAndAdd, isPending: createPending } = useMutation({
    mutationFn: async (title: string) => {
      const { collection } = await createCollection({
        title,
        visibility: 'private',
        isOrdered: false,
      });
      await addCollectionItem(collection._id, { itemId });
      return collection;
    },
    onSuccess: (collection) => {
      queryClient.invalidateQueries({ queryKey: ['collections', 'mine'] });
      queryClient.invalidateQueries({ queryKey: ['collectionsContaining', itemId] });
      toast.success(`Created "${collection.title}" and added item`);
      closePicker();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to create list'),
  });

  const isPending = addPending || createPending || removePending;

  function openPicker() {
    setPickerOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function closePicker() {
    setPickerOpen(false);
    setQuery('');
  }

  // Close on Escape
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closePicker(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [pickerOpen]);

  const isLoading = loadingContains || (pickerOpen && loadingAll);

  if (isLoading) {
    return (
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-6 w-20 rounded-full" />
        <Skeleton className="h-6 w-24 rounded-full" />
        <Skeleton className="h-6 w-16 rounded-full" />
      </div>
    );
  }

  const containingCollections = containsData?.collections ?? [];
  const ownedCollections = allData?.collections ?? [];
  const containsSet = new Set(containingCollections.map((c) => c._id));
  const addableCollections = ownedCollections.filter((c) => !containsSet.has(c._id));

  // Filter addable collections by current query
  const trimmed = query.trim();
  const filtered = trimmed
    ? addableCollections.filter((c) => c.title.toLowerCase().includes(trimmed.toLowerCase()))
    : addableCollections;

  // Show "Create" option only when there's a typed name with no exact match
  const exactMatch = ownedCollections.some(
    (c) => c.title.toLowerCase() === trimmed.toLowerCase()
  );
  const showCreate = trimmed.length > 0 && !exactMatch;

  return (
    <div className="space-y-3">

      {/* Chip row: existing list tags + "+" to open picker */}
      <div className="flex flex-wrap gap-2">
        {containingCollections.map((col) => (
          <span
            key={col._id}
            className="inline-flex items-center gap-1 pl-3 pr-1.5 py-1.5 rounded-full text-xs font-medium border border-border bg-muted/60 hover:bg-muted transition-colors"
          >
            <Link
              href={`/lists/${col._id}`}
              className="hover:text-primary transition-colors cursor-pointer"
            >
              {col.title}
            </Link>
            {col.collectionItemId && (
              <button
                type="button"
                disabled={isPending}
                onClick={() => removeFromCollection({ collectionId: col._id, collectionItemId: col.collectionItemId! })}
                className="ml-0.5 p-0.5 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50 cursor-pointer"
                aria-label={`Remove from ${col.title}`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}

        <button
          type="button"
          onClick={pickerOpen ? closePicker : openPicker}
          aria-expanded={pickerOpen}
          className={cn(
            'inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer',
            pickerOpen
              ? 'border-primary/40 bg-primary/8 text-primary'
              : 'border-dashed border-border text-muted-foreground hover:border-primary/40 hover:text-primary hover:bg-primary/5'
          )}
        >
          {pickerOpen ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
          {pickerOpen ? 'Close' : 'Add to list'}
        </button>
      </div>

      {/* Inline autocomplete picker */}
      {pickerOpen && (
        <div className="border border-border rounded-xl overflow-hidden shadow-sm">
          <div className="px-4 py-2.5 border-b border-border bg-muted/20">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search or create a list…"
              autoComplete="off"
              className="w-full text-sm bg-transparent outline-none placeholder:text-muted-foreground/60"
              disabled={isPending}
            />
          </div>

          <ul className="divide-y divide-border max-h-52 overflow-y-auto">
            {filtered.map((col) => (
              <li key={col._id}>
                <button
                  type="button"
                  disabled={isPending}
                  onClick={() => addToCollection(col._id)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-sm hover:bg-muted/50 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  <span className="truncate font-medium">{col.title}</span>
                  <span className="text-xs text-muted-foreground shrink-0 capitalize px-2 py-0.5 rounded-full bg-muted">
                    {col.visibility}
                  </span>
                </button>
              </li>
            ))}

            {showCreate && (
              <li>
                <button
                  type="button"
                  disabled={isPending}
                  onClick={() => createAndAdd(trimmed)}
                  className="w-full flex items-center gap-2 px-4 py-3 text-left text-sm hover:bg-primary/5 transition-colors disabled:opacity-50 text-muted-foreground cursor-pointer"
                >
                  <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Plus className="h-3 w-3 text-primary" />
                  </div>
                  <span>
                    Create <strong className="text-foreground">&ldquo;{trimmed}&rdquo;</strong>
                  </span>
                </button>
              </li>
            )}

            {filtered.length === 0 && !showCreate && (
              <li className="px-4 py-3 text-sm text-muted-foreground">
                {ownedCollections.length === 0 ? (
                  <>
                    No lists yet —{' '}
                    <Link href="/library" className="underline hover:text-foreground">
                      create one from your Library
                    </Link>
                    .
                  </>
                ) : (
                  'Already in all your lists.'
                )}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
